import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import CommentForm from './CommentForm';
import UserBioTrigger from './UserBioTrigger';
import { useUserBioDialog } from '../hooks/useUserBioDialog';
import '../styles/comment-item.css';

const CommentItem = ({ comment, currentUser, onDeleted, onReply, depth = 0 }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [userReaction, setUserReaction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content_text);
  const { t } = useLanguage();
  const { showUserBio, UserBioDialogComponent } = useUserBioDialog();

  const isOwner = currentUser?.id === comment.author_user_id;
  const canModerate = currentUser?.role === 'admin' || currentUser?.role === 'super-admin';

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return t('news.justNow');
    if (diffMinutes === 1) return t('news.minuteAgo');
    if (diffMinutes < 60) return t('news.minutesAgo', { minutes: diffMinutes });
    if (diffHours === 1) return t('news.hourAgo');
    if (diffHours < 24) return t('news.hoursAgo', { hours: diffHours });
    if (diffDays === 1) return t('news.yesterday');
    if (diffDays < 7) return t('news.daysAgo', { days: diffDays });
    
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchReactions();
  }, [comment.id]);

  const fetchReactions = async () => {
    try {
      const response = await fetch(`/api/content?operation=getEngagement&contentId=${comment.id}`);
      const data = await response.json();
      
      if (response.ok) {
        setReactions(data);
        
        // Check if current user has reacted with any type
        const userReacted = data.find(r => 
          r.user_ids.includes(currentUser?.id)
        );
        setUserReaction(userReacted ? userReacted.engagement_type : null);
      }
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };

  const getReactionCount = (type) => {
    const reaction = reactions.find(r => r.engagement_type === type);
    return reaction ? reaction.count : 0;
  };

  const handleReaction = async (type = 'like') => {
    if (!currentUser) return;

    try {
      const isRemoving = userReaction === type;
      
      // If user has a different reaction, remove it first
      if (userReaction && userReaction !== type) {
        await fetch(`/api/content?operation=removeEngagement`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentId: comment.id,
            userId: currentUser.id,
            engagementType: userReaction
          })
        });
      }
      
      if (!isRemoving) {
        // Add the new reaction
        const response = await fetch(`/api/content?operation=addEngagement`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentId: comment.id,
            userId: currentUser.id,
            engagementType: type
          })
        });

        if (response.ok) {
          setUserReaction(type);
        }
      } else {
        // Remove the current reaction
        const response = await fetch(`/api/content?operation=removeEngagement`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentId: comment.id,
            userId: currentUser.id,
            engagementType: type
          })
        });

        if (response.ok) {
          setUserReaction(null);
        }
      }
      
      fetchReactions();
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('news.comments.deleteConfirm'))) return;

    try {
      const response = await fetch(`/api/content?operation=deleteContent&contentId=${comment.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          userRole: currentUser.role
        })
      });

      if (response.ok) {
        onDeleted(comment.id);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleEdit = async () => {
    try {
      const response = await fetch(`/api/content?operation=updateContent&contentId=${comment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentText: editContent,
          userId: currentUser.id,
          userRole: currentUser.role
        })
      });

      if (response.ok) {
        setIsEditing(false);
        comment.content_text = editContent;
      }
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };

  const handleReplyAdded = (newReply) => {
    onReply(newReply);
    setShowReplyForm(false);
  };


  return (
    <div className={`comment-item depth-${Math.min(depth, 3)}`}>
      <div className="comment-header">
        <div className="comment-author">
          <UserBioTrigger
            userId={comment.author_user_id}
            userName={comment.author_name}
            userEmail={comment.author_email}
            onShowBio={showUserBio}
          >
            <strong>{comment.author_name}</strong>
          </UserBioTrigger>
          <span className="comment-time">
            {formatDate(comment.created_at)}
          </span>
          {comment.updated_at !== comment.created_at && (
            <span className="comment-edited">{t('news.comments.edited')}</span>
          )}
        </div>
        
        {(isOwner || canModerate) && (
          <div className="comment-actions">
            {isOwner && (
              <button 
                className="comment-action edit"
                onClick={() => setIsEditing(!isEditing)}
              >
                ✏️
              </button>
            )}
            <button 
              className="comment-action delete"
              onClick={handleDelete}
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      <div className="comment-content">
        {isEditing ? (
          <div className="comment-edit">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="comment-edit-input"
            />
            <div className="comment-edit-actions">
              <button onClick={handleEdit}>{t('news.comments.save')}</button>
              <button onClick={() => setIsEditing(false)}>{t('news.comments.cancel')}</button>
            </div>
          </div>
        ) : (
          <div className="comment-text">
            {comment.content_text}
          </div>
        )}
      </div>

      <div className="comment-footer">
        <div className="comment-reactions">
          <button
            className={`reaction-button ${userReaction === 'like' ? 'active' : ''}`}
            onClick={() => handleReaction('like')}
            title="Like"
          >
            👍 {getReactionCount('like') > 0 && getReactionCount('like')}
          </button>
          <button
            className={`reaction-button ${userReaction === 'love' ? 'active' : ''}`}
            onClick={() => handleReaction('love')}
            title="Love"
          >
            ❤️ {getReactionCount('love') > 0 && getReactionCount('love')}
          </button>
          <button
            className={`reaction-button ${userReaction === 'celebrate' ? 'active' : ''}`}
            onClick={() => handleReaction('celebrate')}
            title="Celebrate"
          >
            🎉 {getReactionCount('celebrate') > 0 && getReactionCount('celebrate')}
          </button>
          <button
            className={`reaction-button ${userReaction === 'helpful' ? 'active' : ''}`}
            onClick={() => handleReaction('helpful')}
            title="Helpful"
          >
            💡 {getReactionCount('helpful') > 0 && getReactionCount('helpful')}
          </button>
          <button
            className={`reaction-button ${userReaction === 'amazing' ? 'active' : ''}`}
            onClick={() => handleReaction('amazing')}
            title="Amazing"
          >
            🤩 {getReactionCount('amazing') > 0 && getReactionCount('amazing')}
          </button>
        </div>

        {currentUser && depth < 5 && (
          <button
            className="reply-button"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            {t('news.comments.reply')}
          </button>
        )}
      </div>

      {showReplyForm && (
        <div className="reply-form">
          <CommentForm
            contentId={comment.id}
            onCommentAdded={handleReplyAdded}
            isReply={true}
          />
        </div>
      )}
      
      {UserBioDialogComponent}
    </div>
  );
};

export default CommentItem;